/**
 * Retry utility for handling transient network errors
 */

import axios from 'axios';
import {
  RETRYABLE_ERROR_CODES,
  RETRYABLE_ERROR_PATTERNS,
} from 'src/contstant/commonLabels';

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 2) */
  maxRetries?: number;
  /** Base delay in milliseconds between retries (default: 100) */
  baseDelayMs?: number;
  /** Whether to use exponential backoff (default: true) */
  exponentialBackoff?: boolean;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'onRetry'>> = {
  maxRetries: 2,
  baseDelayMs: 100,
  exponentialBackoff: true,
};

/**
 * Checks if an error is retryable (transient network issues that might succeed on retry).
 * Retries: aborted/cancelled requests, network errors, timeouts.
 * Does NOT retry: 4xx/5xx HTTP errors, business logic errors.
 *
 * Uses axios utilities for reliable error detection across browsers.
 */
export const isRetryableError = (error: unknown): boolean => {
  if (!error) return false;

  // Use axios built-in check for cancelled requests
  if (axios.isCancel(error)) return true;

  // Check axios errors
  if (axios.isAxiosError(error)) {
    // Network errors (no response received)
    if (!error.response) {
      const code = error.code;
      // Axios error codes for network/timeout issues
      if (
        code === RETRYABLE_ERROR_CODES.ERR_CANCELED ||
        code === RETRYABLE_ERROR_CODES.ECONNABORTED ||
        code === RETRYABLE_ERROR_CODES.ERR_NETWORK ||
        code === RETRYABLE_ERROR_CODES.ETIMEDOUT
      ) {
        return true;
      }
      // Request was made but no response (network failure)
      if (error.request) return true;
    }
    // Don't retry HTTP errors (4xx, 5xx) - they have a response
    return false;
  }

  // Fallback for non-axios errors (e.g., TypeScript client)
  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // XMLHttpRequest failure pattern (status 0 = request never completed)
    if (err.status === 0 && !err.data) return true;

    // Check error message for browser-specific patterns
    if (typeof err.message === 'string') {
      const message = err.message.toLowerCase();
      if (RETRYABLE_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Wraps an async function with retry logic for transient errors.
 * Only retries on network issues, aborted requests, and timeouts.
 *
 * @param fn - Async function to execute with retry
 * @param options - Retry configuration options
 * @returns Promise resolving to the function result
 * @throws The last error if all retries fail
 *
 * @example
 * // Basic usage
 * const result = await withRetry(() => userService.getAll(data));
 *
 * @example
 * // With custom options
 * const result = await withRetry(
 *   () => apiHeliosClient.send(new SomeCommand(input)),
 *   { maxRetries: 3, baseDelayMs: 200 }
 * );
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  options?: RetryOptions,
): Promise<T> => {
  const { maxRetries, baseDelayMs, exponentialBackoff } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      lastError = error;

      // Only retry on retryable errors and if we have attempts left
      if (isRetryableError(error) && attempt < maxRetries) {
        const delay = exponentialBackoff
          ? baseDelayMs * Math.pow(2, attempt)
          : baseDelayMs;
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Non-retryable error or out of retries - throw immediately
      throw error;
    }
  }

  // Should not reach here, but TypeScript needs this
  throw lastError;
};
